/**
 * EcuMergeModal — wizard to merge an external ECU log onto a session.
 *
 * Step 1: upload the ECU log (CSV or a packaged/standalone MaxxECU log).
 * Step 2: review the proposed channel mapping (with post-align correlation as
 *         the truth column), verify the alignment on a sync-channel overlay,
 *         optionally nudge the offset, then commit.
 *
 * Only NON-shared channels are merged; shared channels (checked) are used for
 * alignment only. A row's correlation is the real arbiter — name matching just
 * seeds the proposal.
 *
 * Two entry points. From the analysis page it opens on step 1 against the
 * loaded session. From the Upload page's match flow it is handed `staged`
 * ({ stagingId, sourceFilename }) and skips straight to step 2, reviewing a log
 * the server already holds — so this file stays the one place the mapping and
 * overlay live.
 */
import { useState, useCallback, useRef, useMemo, useEffect } from 'react';
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import { ecuPreview, ecuCommit } from '../../services/api';
import styles from './EcuMergeModal.module.css';

function corrColor(r) {
  if (r == null) return '#888';
  if (r >= 0.8) return '#3ddc84';
  if (r >= 0.5) return '#f5c842';
  return '#ff6b6b';
}

// Roll an array by k samples (positive k shifts right), filling exposed ends
// with null — used to preview a manual offset nudge without re-fetching data.
function rollSeries(arr, k) {
  const n = arr.length;
  if (!k) return arr;
  const out = new Array(n).fill(null);
  for (let i = 0; i < n; i++) {
    const j = i - k;
    if (j >= 0 && j < n) out[i] = arr[j];
  }
  return out;
}

export default function EcuMergeModal({ sessionId, sessionName, staged, onClose, onMerged }) {
  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [file, setFile] = useState(null);
  const [uploadPct, setUploadPct] = useState(null); // 0–100 during transfer, null otherwise
  const [preview, setPreview] = useState(null);
  // Per-ecu-channel override of the "treat as shared (skip)" flag.
  const [sharedOverride, setSharedOverride] = useState({});
  const [nudge, setNudge] = useState(0); // seconds added to auto offset
  const fileInputRef = useRef(null);

  // ── Step 1: upload + preview ──────────────────────────────────────────────
  const runPreview = useCallback(async (fileOrStaged) => {
    setBusy(true); setError(null); setUploadPct(0);
    try {
      const pv = await ecuPreview(sessionId, fileOrStaged, pct => setUploadPct(pct));
      setPreview(pv);
      const init = {};
      pv.matches.forEach(m => { init[m.ecu_channel] = m.is_shared; });
      setSharedOverride(init);
      setNudge(0);
      setStep(2);
    } catch (e) {
      setError(String(e.message || e));
    } finally {
      setBusy(false);
      setUploadPct(null);
    }
  }, [sessionId]);

  // Opened from the match flow: the log is already on the server, so there is
  // nothing to upload — go straight to the review step.
  useEffect(() => {
    if (staged?.stagingId) runPreview(staged);
    // Re-running on a new staged log is the point; runPreview is stable per session.
  }, [staged?.stagingId, runPreview]); // eslint-disable-line react-hooks/exhaustive-deps

  const onPickFile = (f) => { if (f) { setFile(f); runPreview(f); } };

  // ── Derived: which ecu channels are currently marked shared (skipped) ──────
  const sharedSet = useMemo(() => {
    const s = new Set();
    Object.entries(sharedOverride).forEach(([ch, on]) => { if (on) s.add(ch); });
    return s;
  }, [sharedOverride]);

  const addedCount = useMemo(() => {
    if (!preview) return 0;
    // new = every ecu channel not currently marked shared
    const allEcu = new Set([
      ...preview.matches.map(m => m.ecu_channel),
      ...preview.new_channels,
    ]);
    let n = 0;
    allEcu.forEach(ch => { if (!sharedSet.has(ch)) n++; });
    return n;
  }, [preview, sharedSet]);

  // ── Overlay chart data (with nudge applied to the ECU line) ────────────────
  const chartData = useMemo(() => {
    if (!preview) return [];
    const { times, session_values, ecu_values } = preview.sync_trace;
    const dt = times.length > 1 ? (times[times.length - 1] - times[0]) / (times.length - 1) : 0.04;
    const k = dt ? Math.round(nudge / dt) : 0;
    const ecuRolled = rollSeries(ecu_values, k);
    return times.map((t, i) => ({ t, session: session_values[i], ecu: ecuRolled[i] }));
  }, [preview, nudge]);

  // ── Commit ────────────────────────────────────────────────────────────────
  const commit = useCallback(async () => {
    if (!preview) return;
    setBusy(true); setError(null);
    try {
      const resp = await ecuCommit(sessionId, {
        staging_id: preview.staging_id,
        source_filename: preview.source_filename,
        offset_s: preview.offset_s + nudge,
        sec_per_sample: preview.sec_per_sample,
        sync_channel: preview.sync_channel,
        shared_ecu_channels: Array.from(sharedSet),
        // Echo the measured confidence back: the server has no way to
        // recompute it once we supply the alignment ourselves.
        confidence: preview.confidence,
      });
      onMerged?.(resp);
      onClose?.();
    } catch (e) {
      setError(String(e.message || e));
    } finally {
      setBusy(false);
    }
  }, [preview, nudge, sharedSet, sessionId, onMerged, onClose]);

  return (
    <div className={styles.overlay} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className={styles.modal}>
        <div className={styles.header}>
          <h2 className={styles.title}>
            Add ECU Data
            {sessionName && <span className={styles.subtitle}> → {sessionName}</span>}
          </h2>
          <button className={styles.closeBtn} onClick={onClose} aria-label="Close">×</button>
        </div>

        {error && <div className={styles.errorBar}>{error}</div>}

        {step === 1 && (
          <div className={styles.body}>
            <p className={styles.hint}>
              Upload a log from your engine computer — a CSV export, a MaxxECU{' '}
              <code>.MaxxECU-Zip-log</code> package, or a standalone{' '}
              <code>.maxxlog</code>. Shared channels (RPM, throttle…) are used to
              time-align it with this session; the remaining channels are added
              to the session.
            </p>
            {busy ? (
              <div className={styles.dropzone}>
                <span className={styles.dropHint}>
                  {uploadPct != null && uploadPct < 100
                    ? `Uploading ${file?.name || 'file'}… ${uploadPct}%`
                    : 'Aligning… this can take a few seconds'}
                </span>
                <div className={styles.progressBar}>
                  <div
                    className={`${styles.progressFill} ${uploadPct >= 100 ? styles.progressIndeterminate : ''}`}
                    style={uploadPct != null && uploadPct < 100 ? { width: `${uploadPct}%` } : undefined}
                  />
                </div>
              </div>
            ) : (
              <div
                className={styles.dropzone}
                onClick={() => fileInputRef.current?.click()}
                onDragOver={e => e.preventDefault()}
                onDrop={e => { e.preventDefault(); onPickFile(e.dataTransfer.files?.[0]); }}
              >
                <input
                  ref={fileInputRef} type="file" hidden
                  accept=".csv,.txt,.tsv,.zip,.MaxxECU-Zip-log,.MaxxECU-log,.maxxlog"
                  onChange={e => onPickFile(e.target.files?.[0])}
                />
                <span className={styles.dropHint}>
                  {file ? file.name : 'Drop an ECU log here or click to browse'}
                </span>
              </div>
            )}
          </div>
        )}

        {step === 2 && preview && (
          <div className={styles.body}>
            {/* ── Alignment summary ── */}
            <div className={`${styles.summary} ${preview.low_confidence ? styles.summaryWarn : ''}`}>
              <div className={styles.summaryItem}>
                <span className={styles.summaryLabel}>Sync channel</span>
                <span className={styles.summaryValue}>{preview.sync_channel}</span>
              </div>
              <div className={styles.summaryItem}>
                <span className={styles.summaryLabel}>Confidence</span>
                <span className={styles.summaryValue} style={{ color: corrColor(preview.confidence) }}>
                  {(preview.confidence * 100).toFixed(1)}%
                </span>
              </div>
              <div className={styles.summaryItem}>
                <span className={styles.summaryLabel}>Detected rate</span>
                <span className={styles.summaryValue}>{preview.rate_hz} Hz</span>
              </div>
              <div className={styles.summaryItem}>
                <span className={styles.summaryLabel}>Offset</span>
                <span className={styles.summaryValue}>{(preview.offset_s + nudge).toFixed(2)} s</span>
              </div>
              <div className={styles.summaryItem}>
                <span className={styles.summaryLabel}>Channels to add</span>
                <span className={styles.summaryValue}>{addedCount}</span>
              </div>
            </div>
            {preview.low_confidence && (
              <div className={styles.warnText}>
                ⚠ Low automatic confidence — verify the overlay below and nudge the
                offset until the two traces line up before committing.
              </div>
            )}

            {/* ── Sync overlay ── */}
            <div className={styles.chartWrap}>
              <ResponsiveContainer width="100%" height={180}>
                <LineChart data={chartData} accessibilityLayer={false} margin={{ top: 8, right: 12, bottom: 4, left: 0 }}>
                  <XAxis dataKey="t" tick={{ fontSize: 10, fill: '#999' }}
                         tickFormatter={v => v.toFixed(0)} />
                  <YAxis tick={{ fontSize: 10, fill: '#999' }} width={40} />
                  <Tooltip contentStyle={{ background: '#1c1c22', border: '1px solid #333', fontSize: 12 }}
                           labelFormatter={v => `${Number(v).toFixed(1)} s`} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Line type="monotone" dataKey="session" name={`Session ${preview.sync_channel}`}
                        stroke="#5b9dff" dot={false} isAnimationActive={false} strokeWidth={1.5} />
                  <Line type="monotone" dataKey="ecu" name={`ECU ${preview.sync_channel}`}
                        stroke="#3ddc84" dot={false} isAnimationActive={false} strokeWidth={1.5} />
                </LineChart>
              </ResponsiveContainer>
              <div className={styles.nudgeRow}>
                <span className={styles.nudgeLabel}>Nudge offset</span>
                <button className={styles.nudgeBtn} onClick={() => setNudge(n => +(n - 0.1).toFixed(2))}>−0.1s</button>
                <input
                  className={styles.nudgeSlider} type="range" min={-5} max={5} step={0.05}
                  value={nudge} onChange={e => setNudge(+e.target.value)}
                />
                <button className={styles.nudgeBtn} onClick={() => setNudge(n => +(n + 0.1).toFixed(2))}>+0.1s</button>
                <span className={styles.nudgeValue}>{nudge >= 0 ? '+' : ''}{nudge.toFixed(2)}s</span>
                {nudge !== 0 && <button className={styles.nudgeReset} onClick={() => setNudge(0)}>reset</button>}
              </div>
            </div>

            {/* ── Mapping table ── */}
            <div className={styles.tableWrap}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Shared<br/>(skip)</th>
                    <th>Session channel</th>
                    <th>ECU channel</th>
                    <th>Name</th>
                    <th>Correlation</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.matches.map(m => (
                    <tr key={m.ecu_channel}>
                      <td className={styles.tdCheck}>
                        <input
                          type="checkbox"
                          checked={!!sharedOverride[m.ecu_channel]}
                          onChange={e => setSharedOverride(o => ({ ...o, [m.ecu_channel]: e.target.checked }))}
                        />
                      </td>
                      <td>{m.session_channel}</td>
                      <td>{m.ecu_channel}</td>
                      <td className={styles.tdMono}>{m.name_score?.toFixed(2)}</td>
                      <td className={styles.tdMono} style={{ color: corrColor(m.correlation) }}>
                        {m.correlation == null ? '—' : m.correlation.toFixed(2)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className={styles.tableHint}>
                Checked = shared, used only for alignment (not added). Uncheck a row
                with low correlation to add it as new data instead.
              </p>
            </div>

            <div className={styles.footer}>
              <button className={styles.secondaryBtn} onClick={() => setStep(1)} disabled={busy}>Back</button>
              <button className={styles.primaryBtn} onClick={commit} disabled={busy}>
                {busy ? 'Merging…' : `Add ${addedCount} channels`}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
